import { TierProFilled } from '@heidi/design-system/icons';
import { useEvidenceCapabilities } from '@/atoms/evidenceCapabilitiesAtoms';
import type { SourceControlToggleState } from '@/atoms/sourceControlAtoms';
import {
  clinicianTrialModalOpenAtom,
  clinicianUpgradeModalOpenAtom,
} from '@/atoms/paywallAtoms';
import { IS_EVIDENCE_LIBRARY_ENABLED } from '@/app-shell/flags/evidence-flags';
import {
  isCheckOtherSourceOffered,
  type CheckOtherSourcesBuckets,
} from '@/components/chat/utils/checkOtherSources';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { cn } from '@/lib/cn';
import { isEvidenceApiAvailable } from '@/utils/backendSurfaces';
import { PUBLIC_ROUTES } from '@/utils/publicRoutes';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { BookOpen, ChevronDown, Library, SearchCheck, Stethoscope } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/**
 * Source types available for regeneration.
 *
 * V1: localGuidelines, primaryEvidence, webSearch, myLibrary
 * V2: clinicalSources (= localGuidelines + primaryEvidence combined),
 *     clinicalWeb (explicit web search toggle), myLibrary
 */
export type SourceType =
  | 'localGuidelines'
  | 'primaryEvidence'
  | 'webSearch'
  | 'myLibrary'
  | 'clinicalSources'
  | 'clinicalWeb';

interface SourceOption {
  id: SourceType;
  label: string;
  icon: React.ReactNode;
}

interface RegenerateDropdownProps {
  /** Current source control toggle state - determines which options to show */
  sourcesUsed: SourceControlToggleState;
  /** Region label for local guidelines (e.g., "Australian") */
  regionLabel: string;
  /** Callback when user selects a source to regenerate with */
  onRegenerateWithSource: (source: SourceType) => void;
  /** Called when the dropdown is opened (the "Check other sources" trigger click) */
  onOpen?: () => void;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  sourceControl: CheckOtherSourcesBuckets;
}

/**
 * Dropdown for the regenerate action that allows users to check other sources.
 * Shows only sources that were NOT used in the original query.
 *
 * Based on Figma design: https://www.figma.com/design/Xpd4SOC8nuSY8x3khvCCJl/Ask-Heidi---Evidence?node-id=5381-229346
 */
export const RegenerateDropdown = ({
  sourcesUsed,
  regionLabel,
  onRegenerateWithSource,
  onOpen,
  disabled = false,
  sourceControl,
}: RegenerateDropdownProps) => {
  const { t } = useTranslations();
  const { hasEvidencePro, isInFreeTrial, hasTrialExpired, freeTrialAvailable } =
    useEvidenceBillingInfo();
  const { webSearch: isWebSearchAvailable } = useEvidenceCapabilities();

  const setStartTrialModalOpen = useSetAtom(clinicianTrialModalOpenAtom);
  const setUpgradeModalOpen = useSetAtom(clinicianUpgradeModalOpenAtom);

  // Open the appropriate modal based on user's trial/subscription state
  const handleProFeatureClick = useCallback(() => {
    if (isInFreeTrial || hasTrialExpired) {
      // Trial active or expired → Clinician upgrade (Evidence Pro trial-ended modal retired)
      setUpgradeModalOpen(true);
    } else if (freeTrialAvailable) {
      // User never had trial - show start trial modal
      setStartTrialModalOpen(true);
    } else {
      // Fallback to start trial modal
      setStartTrialModalOpen(true);
    }
  }, [
    isInFreeTrial,
    hasTrialExpired,
    freeTrialAvailable,
    setStartTrialModalOpen,
    setUpgradeModalOpen,
  ]);

  const router = useRouter();
  const isPublicRoute = PUBLIC_ROUTES.some((route) =>
    router.pathname.startsWith(route)
  );

  // The library lives behind the `/evidence` aggregate, which an on-prem server
  // does not mount — so the option is withheld there however the flag resolves.
  const isLibraryEnabled =
    isEvidenceApiAvailable && IS_EVIDENCE_LIBRARY_ENABLED() && !isPublicRoute;

  const isProFeature = (optionId: SourceType) =>
    optionId === 'myLibrary' ||
    optionId === 'clinicalSources';

  const availableOptions: SourceOption[] = [];

  // "Clinical web" and "Clinical sources" both re-ask with web retrieval, so
  // neither is offered where the deployment cannot search the web (F9,
  // MOD-1726) — which on an appliance, with the library option closed above,
  // leaves no option and hides the dropdown (and with it the Pro badge and its
  // trial-modal click).
  if (
    isWebSearchAvailable &&
    !sourcesUsed.webSearch &&
    isCheckOtherSourceOffered('clinicalWeb', sourceControl)
  ) {
    availableOptions.push({
      id: 'clinicalWeb',
      label: t('chat.regenerate.clinicalWeb'),
      icon: <Stethoscope className="size-4" />,
    });
  }

  const isClinicalSourcesOn =
    sourcesUsed.localGuidelines && sourcesUsed.primaryEvidence;
  if (
    isWebSearchAvailable &&
    !isClinicalSourcesOn &&
    isCheckOtherSourceOffered('clinicalSources', sourceControl)
  ) {
    availableOptions.push({
      id: 'clinicalSources',
      label: t('chat.regenerate.clinicalSources'),
      icon: <BookOpen className="size-4" />,
    });
  }

  if (
    !sourcesUsed.myLibrary &&
    isLibraryEnabled &&
    isCheckOtherSourceOffered('myLibrary', sourceControl)
  ) {
    availableOptions.push({
      id: 'myLibrary',
      label: t('chat.regenerate.myLibrary'),
      icon: <Library className="size-4" />,
    });
  }

  // If no alternatives available, don't render the dropdown
  if (availableOptions.length === 0 || isPublicRoute) {
    return null;
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) onOpen?.();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          disableAutomaticSvgSizing
          variant="ghost"
          size="md"
          disabled={disabled}
          aria-label={t('chat.regenerate.checkOtherSources')}
          className="group inline-flex min-w-0 max-w-full gap-1.5 text-text-secondary transition-colors hover:text-text-primary evidence-panel-narrow:w-9 evidence-panel-narrow:min-w-9 evidence-panel-narrow:max-w-none evidence-panel-narrow:justify-center evidence-panel-narrow:gap-0 evidence-panel-narrow:px-2.5"
        >
          <SearchCheck className="size-4 shrink-0" />
          <span className="min-w-0 truncate text-left evidence-panel-narrow:hidden">
            {t('chat.regenerate.checkOtherSources')}
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 evidence-panel-narrow:hidden" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={4}
        className={cn(
          // Origin-aware animation: scale from trigger
          'origin-top-left',
          // Smooth scale-in animation
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'duration-200 ease-out'
        )}
      >
        {availableOptions.map((option) => {
          const needsPro = isProFeature(option.id) && !hasEvidencePro;

          return (
            <DropdownMenuItem
              key={option.id}
              onClick={() =>
                needsPro ? handleProFeatureClick() : onRegenerateWithSource(option.id)
              }
              className="gap-2"
            >
              {option.icon}
              {option.label}
              {needsPro && (
                <TierProFilled
                  className="ml-auto size-3 shrink-0 text-tier-pro"
                  aria-hidden={false}
                  aria-label="Pro"
                />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
